import Toast from 'react-native-root-toast'
import { MessageType } from '../../../utils/message-type'
import { useTranslation } from 'react-i18next'
import {
  useCreateNodeRequestMutation,
  useUpgradeNodeTierMutation,
} from '../../../generated/graphql'
import { useCallback, useState } from 'react'
import * as Sentry from '@sentry/react-native'

export const useHandleTierAction = ({
  name,
  description,
  isPrivate,
  allowExtraSeats,
}: {
  name: string
  description: string
  isPrivate: boolean
  allowExtraSeats: boolean
}) => {
  const { t } = useTranslation()
  const [createNode] = useCreateNodeRequestMutation()
  const [upgradeNodeTier] = useUpgradeNodeTierMutation()
  const [billingInput, setBillingInput] = useState<{
    billingId: string
    amount: number
    basePrice: number
  } | null>(null)

  const handleCreate = useCallback(
    async (payload: { name: string; seats: number; basePrice: number }) => {
      if (!name || !description) {
        Toast.show(
          t('Node name and description are required'),
          MessageType.error
        )
        return
      }
      try {
        const { data } = await createNode({
          variables: {
            input: {
              tierName: payload.name,
              seats: payload.seats,
              isPrivate,
              nodeName: name,
              nodeDescription: description,
              allowExtraSeats,
            },
          },
        })
        const { billingId, amount, basePrice } = data?.createNodeRequest!
        setBillingInput({
          billingId,
          amount,
          basePrice: basePrice || payload.basePrice,
        })
      } catch (e: any) {
        Toast.show(e.message, MessageType.error)
        Sentry.captureException(e)
        return
      }
    },
    [name, description, isPrivate, allowExtraSeats]
  )
  const handleUpgrade = useCallback(
    async (payload: {
      nodeTierId: string
      seats: number
      basePrice: number
    }) => {
      try {
        const { data } = await upgradeNodeTier({
          variables: {
            input: {
              nodeTierId: payload.nodeTierId,
              seats: payload.seats,
            },
          },
        })
        const { billingId, amount } = data?.upgradeNodeTier!
        setBillingInput({ billingId, amount, basePrice: payload.basePrice })
      } catch (e: any) {
        Toast.show(e.message, MessageType.error)
        Sentry.captureException(e)
        return
      }
    },
    [upgradeNodeTier]
  )

  const handleAction = async (
    action: 'start' | 'upgrade' | 'downgrade',
    payload: {
      name: string
      seats: number
      basePrice: number
      nodeTierId?: string
    }
  ) => {
    if (action === 'start') {
      return handleCreate(payload)
    }
    if (action === 'upgrade' && payload.nodeTierId) {
      return handleUpgrade({
        nodeTierId: payload.nodeTierId,
        seats: payload.seats,
        basePrice: payload.basePrice,
      })
    }
  }
  const clearBilling = useCallback(() => {
    setBillingInput(null)
  }, [])

  const setBilling = useCallback(
    (billingId: string, amount: number, basePrice: number) => {
      setBillingInput({ billingId, amount, basePrice })
    },
    []
  )

  return {
    clearBilling,
    handleAction,
    billingInput,
    setBilling,
  }
}
